import { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';
import { Languages, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface Language {
    code: string;
    name: string;
    nativeName: string;
}

const LANGUAGES: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
];

/**
 * Language Switcher Component
 * Allows users to switch between English and Arabic
 */
export const LanguageSwitcher = () => {
    const { i18n, t } = useTranslation();
    const { user } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [loading, setLoading] = useState(false);
    const open = Boolean(anchorEl);

    const currentLanguage =
        LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = async (languageCode: string) => {
        if (languageCode === i18n.language) {
            handleClose();
            return;
        }

        setLoading(true);
        try {
            // Change language in i18next
            await i18n.changeLanguage(languageCode);

            // Persist to backend if user is logged in
            if (user) {
                await api.put('/user/language', { language: languageCode });
            }

            // Store in localStorage as fallback
            localStorage.setItem('language', languageCode);

            toast.success(t('messages.success.saved'));
        } catch (error) {
            console.error('Error changing language:', error);
            toast.error(t('messages.error.general'));
            // Revert language change on error
            await i18n.changeLanguage(currentLanguage.code);
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    return (
        <>
            <Tooltip title={t('common.language')}>
                <IconButton
                    onClick={handleClick}
                    size="small"
                    color="inherit"
                    aria-label={t('common.changeLanguage')}
                    aria-controls={open ? 'language-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    disabled={loading}
                >
                    <Languages size={20} />
                </IconButton>
            </Tooltip>

            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        mt: 1.5,
                        minWidth: 180,
                    },
                }}
            >
                {LANGUAGES.map((language) => (
                    <MenuItem
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        selected={language.code === i18n.language}
                        disabled={loading}
                    >
                        {language.code === i18n.language && (
                            <ListItemIcon>
                                <Check size={18} />
                            </ListItemIcon>
                        )}
                        <ListItemText
                            inset={language.code !== i18n.language}
                            primary={language.nativeName}
                            secondary={language.name}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
